/**
 * S1 (C3 reconciled build plan — docs/architecture/REALTIME-STREAMING.md):
 * ONE shared ingestion handler for unified-WS frames.
 *
 * Both ingestion paths route through `handleUnifiedMessage`:
 *   - the LEADER tab's real socket (`connection-unified.ts` onmessage), and
 *   - FOLLOWER tabs receiving raw frames relayed over the TabCoordinator (S4)
 * so every tab applies identical cache semantics.
 *
 * Invalidation is SCOPED per channel (S1 kills the old 10-key
 * `invalidateTradingTruth` blast): a notification or account message never
 * nukes positions/orders/fills/pnl, and a positions burst reconciles only the
 * position-derived truth. The positions payload-apply keeps its trailing
 * 1500ms debounce (burst of N deltas → ONE reconciliation refetch).
 *
 * ECHO-LOOP GUARD (S4 contract): this module must NEVER invoke the tab relay.
 * Only the leader's socket wrapper relays frames; a handler-level relay would
 * re-broadcast relayed frames and loop.
 */

import { websocketAlertsService } from "@/services/websocket-alerts.service";
import { useNotificationStore } from "@/features/notifications/store/notification-store";
import { handleExecutionUpdates } from "./handle-execution-updates";
import { captureAlertToast, shouldToastOnce } from "./toast-once";
import {
  handleMarketDataMessage,
} from "./handle-market-updates";
import {
  applyEmergencyChannelMessage,
  applyPositionsChannelMessage,
  makeTrailingInvalidator,
} from "./apply-trading-events";
import {
  bumpGlobalFlattenConnectionEpoch,
  markGlobalFlattenUnknown,
} from "@/features/positions/utils/global-flatten-safe-state";
import { applyCopyStatusSnapshot } from "@/features/groups/services/copy-status.service";
import { applyLedgerSnapshot } from "@/features/groups/services/group-ledger.service";
import { agentThoughtToMessage } from "@/features/office/utils/agent-stream";
import { applyOfficeAgentPresence } from "@/features/office/utils/agent-presence";
import { useFlowStore } from "@/features/office/store/flow.store";
import { debugService } from "@/features/debug";
import { useWebSocketStore } from "@/store/use-websocket-store";
import {
  bufferBarrierFrame,
  runSnapshotReconciliation,
} from "./snapshot-barrier";
import type { QueryClient } from "@tanstack/react-query";
import type {
  Notification,
  NotificationPriority,
  NotificationType,
} from "@/features/notifications/types/notification.types";
import { presentMemberAlert } from "@/features/notifications/utils/member-alert-presentation";
import { showPortfolioProposalToast } from "@/features/groups/portfolio-proposal/portfolio-proposal-toast";

type AnyRecord = Record<string, any>;

// publish_alert flattens extra_data onto the payload (kind, member_severity
// live at the top level). The toast path only reads payload.metadata /
// extra_data, so lift those presentation keys or cash-exit advisories
// collapse to a red "trades paused" halt.
const ALERT_PRESENTATION_KEYS = [
  "kind",
  "member_severity",
  "member_guidance",
  "broker_account_id",
  "group_id",
  "open_positions",
  "mechanism",
  "mechanism_label",
  "account_name",
  "realized_pnl",
  "source",
  "source_detail",
  "cash_exit_line",
  "hold_ms",
  "instant_close",
  "celebratory",
  "group_name",
  "symbol",
] as const;

export function liftAlertPresentationMetadata(
  payload: Record<string, unknown>,
  shared?: Record<string, unknown>,
): Record<string, unknown> {
  const nested =
    (payload.metadata as Record<string, unknown> | undefined) ||
    (payload.extra_data as Record<string, unknown> | undefined) ||
    shared ||
    {};
  const lifted: Record<string, unknown> = { ...nested };
  for (const key of ALERT_PRESENTATION_KEYS) {
    if (lifted[key] == null && payload[key] != null) {
      lifted[key] = payload[key];
    }
  }
  return lifted;
}

type WebsocketStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "working"
  | "reconnecting"
  | "error"
  | "live-relayed"; // S4: follower fed by the leader's raw-frame relay

export interface UnifiedWebsocketDataBridge {
  setWebsocketData: (profileId: number, status: WebsocketStatus) => void;
  getWebsocketData: (profileId: number) => AnyRecord;
  updateWebsocketMessage?: (profileId: number, messageType: unknown) => void;
  getAllWebsocketData?: () => AnyRecord;
}

export interface UnifiedMessageContext {
  queryClient: AnyRecord;
  profileId: number;
  subjectScope?: string | null;
  profileName?: string;
  websocketData?: UnifiedWebsocketDataBridge;
}

// Follower relay (S4) contexts may run without a websocketData bridge — the
// execution handler requires one, so degrade to no-ops rather than crash.
const NOOP_WEBSOCKET_DATA: UnifiedWebsocketDataBridge = {
  setWebsocketData: () => undefined,
  getWebsocketData: () => ({}),
};

// ---------------------------------------------------------------------------
// S1 — scoped invalidation sets (replaces the 10-key invalidateTradingTruth)
// ---------------------------------------------------------------------------

/** Query-key scopes, per channel, of the truth each channel actually moves. */
export const CHANNEL_TRUTH_SCOPES: Record<string, string[][]> = {
  // "broker-accounts" is the dashboard KPI row's query (useAccounts) — a fill
  // moves realized P&L on that surface, but nothing ever invalidated it, so
  // the top cards froze until a window refocus (live repro 2026-07-21: manual
  // MNQ fill → positions repainted, KPI cards stale). Position/fill events are
  // the delivery path that provably arrives (the accounts channel is a dead
  // end — BUG-A4), so accounts truth rides these scopes.
  // SIG-416 (Codex Dashboard/Office audit): the MOUNTED dashboard hook is
  // features/accounts/services/accounts.service.ts with key ['accounts',
  // params] — the first fix targeted only the legacy brokers.service key
  // ('broker-accounts'), which no mounted Dashboard/Office consumer observes.
  // Both keys ride the scopes: 'accounts' for the mounted tree, the legacy
  // key for any legacy consumers.
  positions: [
    ["positions"],
    ["profile-positions"],
    ["portfolio-metrics"],
    ["accounts"],
    ["broker-accounts"],
  ],
  orders: [["orders"], ["portfolio-metrics"]],
  fills: [
    ["fills"],
    ["positions"],
    ["profile-positions"],
    ["orders"],
    ["portfolio-metrics"],
    ["accounts"],
    ["broker-accounts"],
  ],
  execution: [
    ["positions"],
    ["profile-positions"],
    ["orders"],
    ["fills"],
    ["portfolio-metrics"],
  ],
  reconciliation: [["reconciliation"], ["positions"], ["profile-positions"]],
  // Broker connection health. Deliberately an INVALIDATION and not a
  // client-side status patch: the payload says a broker changed state, so the
  // honest response is to re-read connection truth from the server rather than
  // paint a status the client inferred from one event. Writing the event's own
  // status straight into the cache is how a surface ends up asserting
  // "connected" it never verified.
  broker_health: [
    ["accounts"],
    ["broker-accounts"],
    ["reconciliation", "broker-health"],
  ],
  // pnl.snapshot / copy_trade.completed touch realized pnl + account equity.
  pnl_snapshot: [
    ["positions"],
    ["profile-positions"],
    ["portfolio-metrics"],
    ["accounts"],
    ["broker-accounts"],
    ["profile-accounts"],
    ["account-context"],
  ],
};

// SIG-416: LIVE fills ride the ORDERS channel — event_publisher publishes
// publish_fill_update as "order.fill" and publish_order_update as
// "order.{status}" ("filled" / "partial" carry executions). The backend's
// "fills" channel is snapshot-only (schemas.py VALID_CHANNELS: "There is no
// fills:* Redis pattern — by design"), so the fills truth scope below never
// fired for real traffic and the mounted ['accounts'] observers (dashboard
// KPI row, Office canvas) kept serving pre-fill money truth.
const ORDER_FILL_EVENT_TYPES = new Set([
  "order.fill",
  "order.filled",
  "order.partial",
]);

// W08 D9: the account list is one ~40-row REST read that every fill, position,
// pnl and broker-health frame used to refetch on its own — a connect burst
// re-read it 6-7 times and members hit the 600 reads/min limit (429 storms).
// Account keys now coalesce per QueryClient: one refetch once the burst has
// been quiet for 1.5 s, and never later than 5 s after the first frame, so a
// steady stream cannot starve account truth. Every other key stays immediate.
const ACCOUNT_TRUTH_KEYS: string[][] = [["accounts"], ["broker-accounts"]];
const ACCOUNT_TRUTH_QUIET_MS = 1_500;
const ACCOUNT_TRUTH_MAX_WAIT_MS = 5_000;

type AccountTruthState = {
  readonly schedule: () => void;
  readonly flush: () => void;
  readonly cancel: () => void;
};

const accountTruthByClient = new WeakMap<object, AccountTruthState>();

const accountTruthFor = (queryClient: AnyRecord): AccountTruthState => {
  let state = accountTruthByClient.get(queryClient);
  if (!state) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let firstAt = 0;
    const cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
    };
    const flush = () => {
      cancel();
      for (const queryKey of ACCOUNT_TRUTH_KEYS) {
        queryClient.invalidateQueries({ queryKey });
      }
    };
    const schedule = () => {
      const now = Date.now();
      if (timer) clearTimeout(timer);
      else firstAt = now;
      const wait = Math.max(0, Math.min(ACCOUNT_TRUTH_QUIET_MS, firstAt + ACCOUNT_TRUTH_MAX_WAIT_MS - now));
      timer = setTimeout(flush, wait);
    };
    state = { schedule, flush, cancel };
    accountTruthByClient.set(queryClient, state);
  }
  return state;
};

const isAccountTruthKey = (queryKey: string[]): boolean =>
  queryKey.length === 1 && ACCOUNT_TRUTH_KEYS.some(([key]) => key === queryKey[0]);

const invalidateScope = (
  queryClient: AnyRecord,
  scope: string[][] | undefined,
): void => {
  let accountTruth = false;
  for (const queryKey of scope ?? []) {
    if (isAccountTruthKey(queryKey)) accountTruth = true;
    else queryClient.invalidateQueries({ queryKey });
  }
  if (accountTruth) accountTruthFor(queryClient).schedule();
};

type PositionsReconciliationState = {
  readonly includeFillTruth: () => void;
  readonly schedule: () => void;
};

// WS-B: each QueryClient owns its trailing reconciliation state so one
// dashboard instance cannot redirect or suppress another client's truth.
const positionsReconciliationByClient = new WeakMap<
  object,
  PositionsReconciliationState
>();

const createPositionsReconciliationState = (
  queryClient: AnyRecord,
): PositionsReconciliationState => {
  let fillTruthRequested = false;
  const schedule = makeTrailingInvalidator(() => {
    const scope = fillTruthRequested
      ? CHANNEL_TRUTH_SCOPES.fills
      : CHANNEL_TRUTH_SCOPES.positions;
    fillTruthRequested = false;
    const refetches = (scope ?? [])
      .filter((queryKey) => !isAccountTruthKey(queryKey))
      .map((queryKey) =>
        Promise.resolve(queryClient.invalidateQueries({ queryKey })).catch(
          () => {},
        ),
      );
    // Already a coalesced trailing edge: refresh accounts now and absorb any
    // pending coalesced refresh rather than stacking a second wait on it.
    if ((scope ?? []).some(isAccountTruthKey)) accountTruthFor(queryClient).flush();
    void Promise.all(refetches);
  }, 1500);

  return {
    includeFillTruth: () => {
      fillTruthRequested = true;
    },
    schedule,
  };
};

const schedulePositionsReconciliation = (
  queryClient: AnyRecord,
  includeFillTruth = false,
): void => {
  let state = positionsReconciliationByClient.get(queryClient);
  if (!state) {
    state = createPositionsReconciliationState(queryClient);
    positionsReconciliationByClient.set(queryClient, state);
  }
  if (includeFillTruth) state.includeFillTruth();
  state.schedule();
};

// ---------------------------------------------------------------------------
// F2 (ledger item 12) — a fill must not gate the FIRST paint of an open/flat
// row on the 1.5s trailing reconcile. When `order.filled` arrives and no
// position.* delta was applied around it (mirror-only publish, opened payload
// the client could not construct), the row's only road used to be the
// trailing debounce — measured ~1.5s before the table moved. Fire the
// positions truth refetch NOW on the leading edge. Later fills in the same
// frame coalesce into one follow-up leading batch, preserving first paint
// without restoring the old per-account invalidation fan-out.
// ---------------------------------------------------------------------------

const FILL_LEADING_FRAME_MS = 16;

type FillLeadingFrameState = {
  readonly requestFollowUp: () => void;
};

const fillLeadingFrameByClient = new WeakMap<object, FillLeadingFrameState>();

// Refresh fill-derived surfaces once on the leading edge. Positions and
// group-copy-status first-paint from the WS patch; they ride ONE trailing
// 1.5s reconcile so a 19-leg wave does not cancel/restart /positions per fill.
const FILL_LEADING_KEYS: string[][] = [
  ["orders"],
  ["fills"],
  ["profile-positions"],
  ["portfolio-metrics"],
];

const invalidateFillTruthNow = (queryClient: AnyRecord): void => {
  const refetches = FILL_LEADING_KEYS.map((queryKey) =>
    Promise.resolve(queryClient.invalidateQueries({ queryKey })).catch(
      () => {},
    ),
  );
  void Promise.all(refetches);
};

const scheduleFillLeadingTruth = (queryClient: AnyRecord): void => {
  const activeFrame = fillLeadingFrameByClient.get(queryClient);
  if (activeFrame) {
    activeFrame.requestFollowUp();
    return;
  }

  let followUpRequested = false;
  fillLeadingFrameByClient.set(queryClient, {
    requestFollowUp: () => {
      followUpRequested = true;
    },
  });
  setTimeout(() => {
    fillLeadingFrameByClient.delete(queryClient);
    if (followUpRequested) invalidateFillTruthNow(queryClient);
  }, FILL_LEADING_FRAME_MS);
  invalidateFillTruthNow(queryClient);
};

const reconcilePositionsAfterFill = (queryClient: AnyRecord): void => {
  scheduleFillLeadingTruth(queryClient);
  schedulePositionsReconciliation(queryClient, true);
};

// ---------------------------------------------------------------------------
// Notification normalization (moved verbatim from connection-unified.ts)
// ---------------------------------------------------------------------------

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;

const NOTIFICATION_PRIORITIES = new Set<NotificationPriority>([
  "critical",
  "urgent",
  "high",
  "normal",
  "low",
]);
const NOTIFICATION_TYPES = new Set<NotificationType>([
  "trade_alert",
  "system",
  "risk_warning",
  "achievement",
  "news",
  "trade",
  "agent",
  "emergency",
  "account",
  "strategy",
  "risk",
  "critical_alert",
  // SIG-459: backend vocabulary (schemas/notifications.py) — these were
  // missing, so every live order_placed/warning/sla notification was dropped.
  "general",
  "alert",
  "sla_warning",
  "sla_breach",
  "warning",
  "order_placed",
]);
const NUMERIC_PRIORITY_TO_REST: Record<number, NotificationPriority> = {
  1: "urgent",
  2: "high",
  3: "normal",
  4: "low",
};

const getLiveNotification = (
  value: unknown,
): Record<string, unknown> | null => {
  const root = asRecord(value);
  const payload = asRecord(root?.payload);
  const data = asRecord(root?.data);
  return (
    asRecord(root?.notification) ??
    asRecord(payload?.notification) ??
    asRecord(data?.notification)
  );
};

const warnInvalidLiveNotification = (
  notification: Record<string, unknown>,
  reason: string,
): void => {
  console.warn("Dropping invalid live notification payload", {
    reason,
    notification,
  });
};

const readRequiredString = (
  notification: Record<string, unknown>,
  field: string,
): string | null => {
  const value = notification[field];
  return typeof value === "string" ? value : null;
};

const readNullableString = (
  notification: Record<string, unknown>,
  field: string,
): string | null => {
  const value = notification[field];
  return typeof value === "string" ? value : null;
};

const normalizeNotificationId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const numericId = Number(value);
    return Number.isFinite(numericId) ? numericId : null;
  }
  return null;
};

const normalizeNotificationPriority = (
  value: unknown,
): NotificationPriority | null => {
  if (
    typeof value === "string" &&
    NOTIFICATION_PRIORITIES.has(value as NotificationPriority)
  ) {
    return value as NotificationPriority;
  }
  if (typeof value === "number") {
    return NUMERIC_PRIORITY_TO_REST[value] ?? null;
  }
  return null;
};

const normalizeNotificationType = (value: unknown): NotificationType | null => {
  if (
    typeof value === "string" &&
    NOTIFICATION_TYPES.has(value as NotificationType)
  ) {
    return value as NotificationType;
  }
  // SIG-459: mirror the backend's normalize_notification_type — an unknown
  // string type renders as "system" instead of dropping the notification.
  // Only a non-string (malformed payload) still fails validation.
  if (typeof value === "string" && value.trim()) {
    return "system";
  }
  return null;
};

const normalizeLiveNotification = (
  notification: Record<string, unknown>,
): Notification | null => {
  const id = normalizeNotificationId(notification.id);
  if (id === null) {
    warnInvalidLiveNotification(notification, "missing numeric id");
    return null;
  }

  const notificationType = normalizeNotificationType(
    notification.notification_type,
  );
  if (!notificationType) {
    warnInvalidLiveNotification(
      notification,
      "missing valid notification_type",
    );
    return null;
  }

  const title = readRequiredString(notification, "title");
  if (title === null) {
    warnInvalidLiveNotification(notification, "missing string title");
    return null;
  }

  const message = readRequiredString(notification, "message");
  if (message === null) {
    warnInvalidLiveNotification(notification, "missing string message");
    return null;
  }

  const priority = normalizeNotificationPriority(notification.priority);
  if (!priority) {
    warnInvalidLiveNotification(notification, "missing valid priority");
    return null;
  }

  if (typeof notification.is_read !== "boolean") {
    warnInvalidLiveNotification(notification, "missing boolean is_read");
    return null;
  }

  const createdAt = readRequiredString(notification, "created_at");
  if (createdAt === null) {
    warnInvalidLiveNotification(notification, "missing string created_at");
    return null;
  }

  const extraData =
    asRecord(notification.extra_data) ?? asRecord(notification.metadata) ?? {};
  const updatedAt = readNullableString(notification, "updated_at") ?? createdAt;

  return {
    id,
    notification_type: notificationType,
    title,
    message,
    priority,
    is_read: notification.is_read,
    read_at: readNullableString(notification, "read_at"),
    action_url: readNullableString(notification, "action_url"),
    action_label: readNullableString(notification, "action_label"),
    extra_data: extraData,
    created_at: createdAt,
    updated_at: updatedAt,
  };
};

const routeLiveNotification = (
  value: unknown,
  queryClient: AnyRecord,
): boolean => {
  const liveNotification = getLiveNotification(value);
  if (!liveNotification) {
    return false;
  }

  const notification = normalizeLiveNotification(liveNotification);
  if (!notification) {
    return true;
  }

  const store = useNotificationStore.getState();
  store.addNotification(notification);
  store.setConnected(true);
  queryClient.invalidateQueries({ queryKey: ["notifications"] });
  return true;
};

// Greptile P1 (PR 444): the legacy channels.py snapshot serializer emits
// `read` / `severity` / `metadata` and loose notification_type values
// ("info" | "success" | "warning" | "error"), while the live contract (and
// normalizeLiveNotification) expects `is_read` / `priority` / `extra_data`
// and the REST notification_type set. Without this adapter every legacy
// snapshot entry failed normalization and the whole snapshot dropped to zero.
const LEGACY_TYPE_MAP: Record<string, NotificationType> = {
  info: "system",
  success: "system",
  warning: "risk_warning",
  error: "critical_alert",
  // Greptile P1 round-2 (PR 444): real emitters use these too —
  // tasks/notification_tasks.py send_push_notification defaults to "general"
  // and send_alert_notification writes "alert".
  general: "system",
  alert: "trade_alert",
};
const LEGACY_SEVERITY_TO_PRIORITY: Record<string, NotificationPriority> = {
  critical: "urgent",
  error: "high",
  warning: "high",
  info: "normal",
};

const adaptLegacySnapshotEntry = (
  record: Record<string, unknown>,
): Record<string, unknown> => {
  const isLegacyShape = !("is_read" in record) && "read" in record;
  if (!isLegacyShape) {
    return record;
  }

  const legacyType =
    typeof record.notification_type === "string"
      ? record.notification_type
      : "";
  const severity = typeof record.severity === "string" ? record.severity : "";

  return {
    ...record,
    is_read: record.read === true,
    // Fail OPEN on the type for legacy entries: any unmapped legacy
    // notification_type collapses to "system" rather than dropping the whole
    // notification — the type only drives UI grouping, and losing an entry is
    // worse than a generic bucket (Greptile P1 round-2: "alert"/"general"
    // were still discarded by an exact-map-only approach).
    notification_type: NOTIFICATION_TYPES.has(legacyType as NotificationType)
      ? legacyType
      : (LEGACY_TYPE_MAP[legacyType] ?? "system"),
    priority:
      record.priority ?? LEGACY_SEVERITY_TO_PRIORITY[severity] ?? "normal",
    extra_data: record.extra_data ?? record.metadata ?? {},
  };
};

/**
 * SIG-221: apply a `notifications`-channel message (snapshot/refresh).
 *
 * Behaviour: single-notification envelopes route through
 * routeLiveNotification; snapshot lists are normalized entry-by-entry
 * (invalid entries warn + drop, never throw), merged into the store by id
 * (snapshot wins, newest-first), and the ["notifications"] query is
 * invalidated so the REST infinite query re-syncs the authoritative list.
 * Exported for direct unit testing of the dispatch.
 */
export const applyNotificationsChannelMessage = (
  value: unknown,
  queryClient: AnyRecord,
): void => {
  // Single-notification update carried on the channel.
  if (routeLiveNotification(value, queryClient)) {
    return;
  }

  const root = asRecord(value);
  const payload = root?.payload ?? root?.data;
  const rawList = Array.isArray(payload)
    ? payload
    : (asRecord(payload)?.notifications as unknown);

  const store = useNotificationStore.getState();
  store.setConnected(true);

  if (!Array.isArray(rawList)) {
    // Snapshot without a list (e.g. error payload) — still refresh via REST.
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    return;
  }

  const normalized: Notification[] = [];
  for (const entry of rawList) {
    const record = asRecord(entry);
    if (!record) continue;
    const notification = normalizeLiveNotification(
      adaptLegacySnapshotEntry(record),
    );
    if (notification) {
      normalized.push(notification);
    }
  }

  // One truthful badge: only a server-computed total may write unreadCount.
  // A capped snapshot's length never does (it is not the unread total).
  const serverUnread = asRecord(payload)?.unread_count;
  if (typeof serverUnread === "number" && Number.isFinite(serverUnread)) {
    store.setUnreadCount(serverUnread);
  }

  if (normalized.length > 0) {
    // Merge by id — snapshot wins; keep newest-first ordering (matches the
    // REST list). The count is NOT derived from this list (see above).
    const byId = new Map<Notification["id"], Notification>();
    for (const existing of store.notifications) {
      byId.set(existing.id, existing);
    }
    for (const incoming of normalized) {
      byId.set(incoming.id, incoming);
    }
    const merged = Array.from(byId.values()).sort((a, b) =>
      String(b.created_at).localeCompare(String(a.created_at)),
    );
    store.setNotifications(merged);
  }

  queryClient.invalidateQueries({ queryKey: ["notifications"] });
};

// ---------------------------------------------------------------------------
// Accounts channel (BUG-A4) — moved verbatim from connection-unified.ts
// ---------------------------------------------------------------------------

// BUG-A4: one trailing debouncer for account-truth refetches — a burst of
// cashBalance deltas (e.g. a 19-follower fan-out) schedules exactly ONE
// refetch of the account queries ~1.5s after the burst ends, instead of a
// refetch per message (chatty) or a standing 15s poll (laggy). The debounced
// callback reads the LATEST queryClient (not a closure capture) so a
// recreated client is never invalidated through a stale reference.
let trailingAccountsInvalidate: (() => void) | null = null;
let accountsInvalidateClient: AnyRecord | null = null;

/**
 * BUG-A4: apply an `accounts`-channel message (account.update).
 *
 * Behaviour: direct-patch the matching account's balance into the
 * ['accounts'] cache for an instant equity/realized render, then schedule the
 * trailing (~1500ms) invalidation of the account-truth queries so summary
 * totals and anything the patch can't compute re-sync over REST.
 * Exported for direct unit testing of the dispatch.
 */
export const applyAccountsChannelMessage = (
  value: unknown,
  queryClient: AnyRecord,
): void => {
  const root = asRecord(value);
  const payload = asRecord(root?.payload) ?? asRecord(root?.data);

  if (payload) {
    const accountId =
      typeof payload.account_id === "number" ? payload.account_id : null;
    const brokerAccountId =
      typeof payload.broker_account_id === "string"
        ? payload.broker_account_id
        : null;
    const balance =
      typeof payload.balance === "number"
        ? payload.balance
        : typeof payload.cash_balance === "number"
          ? payload.cash_balance
          : null;

    if (balance !== null && (accountId !== null || brokerAccountId !== null)) {
      queryClient.setQueriesData({ queryKey: ["accounts"] }, (old: unknown) => {
        const cached = asRecord(old);
        if (!cached) return old;

        // SIG-345 bug-class: the live ['accounts'] cache holds the RAW v2
        // response — `items` rows with snake_case fields and a flat STRING
        // balance — while other writers may cache adapted rows under
        // `accounts` (camelCase, balance.amount object) or the legacy
        // `results.accounts` mirror. Patch EVERY copy present; patching
        // only one shape left the rendered copy stale until the 1.5s
        // trailing refetch.
        let touched = false;
        const patchRows = (rows: unknown[]): unknown[] =>
          rows.map((entry: unknown) => {
            const acct = asRecord(entry);
            if (!acct) return entry;
            const matches =
              (accountId !== null && acct.id === accountId) ||
              (brokerAccountId !== null &&
                (acct.accountId === brokerAccountId ||
                  acct.account_id === brokerAccountId));
            if (!matches) return entry;
            touched = true;
            const oldBalance = acct.balance;
            if (oldBalance !== null && typeof oldBalance === "object") {
              return {
                ...acct,
                balance: { ...asRecord(oldBalance), amount: balance },
              };
            }
            // Raw rows carry balance as a string — keep the type so the
            // select/adapter keeps parsing it.
            return {
              ...acct,
              balance:
                typeof oldBalance === "number" ? balance : String(balance),
            };
          });

        const next: Record<string, unknown> = { ...cached };
        let recognized = false;
        if (Array.isArray(cached.accounts)) {
          recognized = true;
          next.accounts = patchRows(cached.accounts);
        }
        if (Array.isArray(cached.items)) {
          recognized = true;
          next.items = patchRows(cached.items);
        }
        const results = asRecord(cached.results);
        if (results && Array.isArray(results.accounts)) {
          recognized = true;
          next.results = {
            ...results,
            accounts: patchRows(results.accounts),
          };
        }
        if (!recognized || !touched) return old;
        return next;
      });
    }
  }

  accountsInvalidateClient = queryClient;
  if (!trailingAccountsInvalidate) {
    trailingAccountsInvalidate = makeTrailingInvalidator(() => {
      const client = accountsInvalidateClient;
      if (!client) return;
      // Covers any coalesced account refresh scheduled since the frame.
      accountTruthFor(client).cancel();
      client.invalidateQueries({ queryKey: ["accounts"] });
      // useBrokerAccounts now delegates to the mounted ['accounts'] query.
      // Do not spend a second burst refetch on the obsolete legacy
      // ['broker-accounts'] key; the account-channel contract is one trailing
      // pass across the four live account-truth families below.
      client.invalidateQueries({ queryKey: ["profile-accounts"] });
      client.invalidateQueries({ queryKey: ["account-context"] });
      client.invalidateQueries({ queryKey: ["portfolio-metrics"] });
    }, 1500);
  }
  // The trailing pass below re-reads ['accounts'] within 1.5 s, so a pending
  // coalesced account refresh is redundant now.
  accountTruthFor(queryClient).cancel();
  trailingAccountsInvalidate();
};

// ---------------------------------------------------------------------------
// The shared routing switch
// ---------------------------------------------------------------------------

const getStoreActions = () => ({
  setLastMarketDataTime: useWebSocketStore.getState().setLastMarketDataTime,
});

// Toast replay guard (halt-toast spam defect): the backend `alerts.initial`
// snapshot re-sends the FULL unread-notification backlog (any age) on every
// alerts-channel (re)subscribe, and the same DB notification can arrive again
// via `alert.update` / `notification` frames. Two session-scoped rules:
//   1. one stable event id toasts ONCE per session, and
//   2. a backlog entry older than the freshness bound never toasts —
// historical criticals belong in the notification center list, not the toast
// stack (a stale "SUBMIT HALT ENGAGED" from yesterday must not re-present as
// a live halt on every reconnect).
const TOAST_MAX_EVENT_AGE_MS = 10 * 60_000;
const toastedEventKeys = new Set<string>();
const copyWaveToastRecipients = new Map<string, Set<string>>();

/** Test-only: reset the session toast-replay guard between test cases. */
export const __resetToastReplayGuardForTests = () => {
  toastedEventKeys.clear();
  copyWaveToastRecipients.clear();
};

/**
 * Route one parsed unified-WS frame into stores + react-query caches.
 *
 * Pure ingestion: no socket lifecycle, no relay, no reconnect logic — those
 * stay in `connection-unified.ts` (leader) and the S4 follower relay wiring.
 */
export const handleUnifiedMessage = (
  data: AnyRecord,
  ctx: UnifiedMessageContext,
): void => {
  const { profileId } = ctx;
  // Mocked clients in tests and the live QueryClient are both accepted; the
  // cast satisfies the stricter consumer signatures (addAlert, appliers).
  const queryClient = ctx.queryClient as QueryClient;
  const messageType = String(data.topic || data.type || data.channel || "");
  const channelType = String(data.channel || "");

  // ---- S2 snapshot barrier --------------------------------------------------
  // subscription.confirmed: the backend's channel subscriptions are live —
  // complete the (re)connect barrier: await refetch of the scoped truth keys,
  // then replay buffered frames in arrival order. On a FOLLOWER (no barrier
  // begun) this reconciles from idle: the leader just reconnected, so this
  // tab's relayed-fed cache is equally stale.
  if (messageType === "subscription.confirmed") {
    bumpGlobalFlattenConnectionEpoch(queryClient);
    void runSnapshotReconciliation(profileId, queryClient, (frame) =>
      handleUnifiedMessage(frame, ctx),
    );
    return;
  }
  // system.resync_required: backend RedisSubscriber recovered — events were
  // lost while the socket stayed open. Same reconciliation (S2/Sol Gap1).
  if (
    messageType === "system.resync_required" ||
    messageType === "resync_required"
  ) {
    bumpGlobalFlattenConnectionEpoch(queryClient);
    void runSnapshotReconciliation(profileId, queryClient, (frame) =>
      handleUnifiedMessage(frame, ctx),
    );
    return;
  }
  if (messageType === "dashboard:performance_update") {
    queryClient.invalidateQueries({ queryKey: ["performanceData"] });
    queryClient.invalidateQueries({ queryKey: ["performance-analytics"] });
    return;
  }
  // While reconciling, trading frames buffer (replayed after the refetch —
  // this is what makes a racing live delta WIN over the REST snapshot).
  if (bufferBarrierFrame(profileId, channelType || messageType, data)) {
    return;
  }
  // ---------------------------------------------------------------------------
  const sharedMetadata =
    (data.metadata as Record<string, unknown> | undefined) ||
    (data.extra_data as Record<string, unknown> | undefined);

  const pushAlert = (
    payload: Record<string, unknown>,
    fallbackTitle = "System Alert",
  ) => {
    const durableNotification = normalizeLiveNotification(payload);
    // `routeLiveNotification` already stored canonical notification frames;
    // pushAlert owns persistence only for the other alert envelope shapes.
    if (durableNotification && messageType !== "notification") {
      const store = useNotificationStore.getState();
      store.addNotification(durableNotification);
      store.setConnected(true);
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    }
    const payloadMetadata = liftAlertPresentationMetadata(
      payload,
      sharedMetadata,
    );
    // SIG-335 severity-split (contract C3): INFO-class connection events
    // are inbox/bell only — never a toast. Route on the backend's
    // explicit severity metadata; kind === "listener_dropped" covers
    // pre-fix rows already persisted without the severity field.
    const routeSeverity =
      typeof payloadMetadata?.severity === "string"
        ? payloadMetadata.severity
        : undefined;
    const eventKind =
      typeof payloadMetadata?.kind === "string"
        ? payloadMetadata.kind
        : undefined;
    if (routeSeverity === "info" || eventKind === "listener_dropped") {
      return;
    }
    const memberPresentation = presentMemberAlert({
      notification_type:
        typeof payload.notification_type === "string"
          ? payload.notification_type
          : typeof payload.alert_type === "string"
            ? payload.alert_type
            : undefined,
      title: String(payload.title || fallbackTitle),
      message: String(payload.message || payload.description || ""),
      priority:
        typeof payload.priority === "string"
          ? payload.priority
          : typeof payload.severity === "string"
            ? payload.severity
            : undefined,
      extra_data: payloadMetadata,
      metadata: payloadMetadata,
      severity:
        typeof payload.severity === "string" ? payload.severity : undefined,
    });
    if (!memberPresentation.toast) {
      return;
    }
    // Stable-id dedup: the same DB notification (snapshot replay, live echo)
    // toasts once per session. Key includes the title so unrelated domains
    // sharing a numeric id (e.g. a reward vs a notification) never collide.
    const rawEventId = payload.id;
    const durableEventKey =
      typeof rawEventId === "number" ||
      (typeof rawEventId === "string" && rawEventId)
        ? messageType === "notification" ||
            (messageType === "alerts.initial" &&
              typeof payload.notification_type === "string")
          ? `notification:${rawEventId}`
          : `${rawEventId}|${String(payload.title || fallbackTitle)}`
        : null;
    const copyWaveKey =
      typeof payloadMetadata?.copy_wave_key === "string" &&
      payloadMetadata.copy_wave_key
        ? payloadMetadata.copy_wave_key
        : null;
    const copyGroupId = payloadMetadata?.group_id;
    const hasCopyGroupId =
      (typeof copyGroupId === "number" && Number.isFinite(copyGroupId)) ||
      (typeof copyGroupId === "string" && copyGroupId);
    const copyWaveToastKey =
      copyWaveKey !== null && hasCopyGroupId && eventKind
        ? `copy-wave:${String(copyGroupId)}:${copyWaveKey}:${eventKind}`
        : null;
    const eventKey = copyWaveToastKey ?? durableEventKey;
    let copyWaveCount: number | null = null;
    if (copyWaveToastKey !== null) {
      const brokerAccountId = payloadMetadata?.broker_account_id;
      const recipientKey =
        (typeof brokerAccountId === "number" ||
          (typeof brokerAccountId === "string" && brokerAccountId))
          ? `account:${String(brokerAccountId)}`
          : durableEventKey !== null
            ? `notification:${durableEventKey}`
            : null;
      if (recipientKey !== null) {
        const recipients =
          copyWaveToastRecipients.get(copyWaveToastKey) ?? new Set<string>();
        recipients.add(recipientKey);
        copyWaveToastRecipients.set(copyWaveToastKey, recipients);
        copyWaveCount = recipients.size;
      }
    }
    if (eventKey !== null && toastedEventKeys.has(eventKey)) {
      return;
    }
    // Freshness bound: an event that carries its own creation time and is
    // older than the bound is history, not news — inbox/bell only, no toast.
    const createdRaw = payload.triggered_at ?? payload.created_at;
    const createdMs =
      typeof createdRaw === "string" ? Date.parse(createdRaw) : Number.NaN;
    if (
      Number.isFinite(createdMs) &&
      Date.now() - createdMs > TOAST_MAX_EVENT_AGE_MS
    ) {
      if (eventKey !== null) {
        toastedEventKeys.add(eventKey);
      }
      return;
    }
    const rawDescription = String(payload.message || payload.description || "");
    // Id-less live frames (publish_alert has no DB id): identical content
    // toasts once per window — kills the per-account risk-alert storms
    // (e.g. cash-exit ownership repair × 15 accounts, 2026-08-14).
    if (
      eventKey === null &&
      // profileId in the key: identical copy on DIFFERENT profiles is two
      // facts — cross-profile suppression hid one of them.
      !shouldToastOnce(
        `alert:${profileId}|${String(payload.title || fallbackTitle)}|${rawDescription}`,
      )
    ) {
      return;
    }
    if (eventKey !== null) {
      toastedEventKeys.add(eventKey);
    }
    const toastTitle =
      copyWaveCount === null
        ? String(payload.title || fallbackTitle)
        : `Copy fill confirmed for ${copyWaveCount} account${copyWaveCount === 1 ? "" : "s"}`;
    if (memberPresentation.variant === "destructive") {
      captureAlertToast(toastTitle, payload);
    }
    websocketAlertsService.addAlert(queryClient, {
      title: toastTitle,
      description: rawDescription
        ? `${rawDescription}\n${memberPresentation.guidance}`
        : memberPresentation.guidance,
      // Member severity wins over raw "critical" so idle disconnects are
      // not red. Real halts still come through as destructive.
      variant: memberPresentation.variant,
      profileId,
      eventType: messageType,
      metadata: payloadMetadata,
    });
  };

  if (
    messageType === "notification" &&
    routeLiveNotification(data, queryClient)
  ) {
    const notificationEnvelope =
      (data.payload as Record<string, unknown> | undefined) ??
      (data.data as Record<string, unknown> | undefined) ??
      data;
    if (notificationEnvelope.notification_update === true) {
      return;
    }
    // SV-CR-4 (Codex adversarial review 2026-07-20): the live path stored
    // the notification in the inbox and returned — so a MONEY-RISK event
    // (e.g. "Broker connection expired") never toasted, violating
    // contract C3 (money-risk = toast + inbox + bell). Info-class still
    // returns silently; pushAlert re-applies the severity gate itself.
    const livePayload = notificationEnvelope.notification as Record<
      string,
      unknown
    >;
    const liveMetadata =
      (livePayload.metadata as Record<string, unknown> | undefined) ||
      (livePayload.extra_data as Record<string, unknown> | undefined);
    // PC-1: a Portfolio Mode proposal carries no `severity` (it is a decision,
    // not a risk alert) — its toast is the card door, never the generic lane.
    if (showPortfolioProposalToast(livePayload)) {
      return;
    }
    const liveSeverity =
      typeof liveMetadata?.severity === "string"
        ? liveMetadata.severity
        : undefined;
    if (liveSeverity && liveSeverity !== "info") {
      pushAlert(livePayload, "Alert");
    }
    return;
  }

  if (messageType === "alerts.initial" && Array.isArray(data.payload)) {
    for (const entry of data.payload) {
      if (entry && typeof entry === "object") {
        pushAlert(entry as Record<string, unknown>, "Alert");
      }
    }
    return;
  }

  // SIG-1215: a community win broadcast is an alert-class frame — route it
  // through pushAlert (dedup + freshness + presentation) into the alerts
  // store; the desk-toast lane renders it, and the winner's own client
  // suppresses it against their private celebration.
  if (messageType === "system.community_win") {
    const payload =
      (data.payload as Record<string, unknown> | undefined) ||
      (data.data as Record<string, unknown> | undefined) ||
      data;
    pushAlert(payload, "Community win");
    return;
  }

  if (messageType === "alert.update" || messageType.startsWith("alert.")) {
    const payload =
      (data.payload as Record<string, unknown> | undefined) ||
      (data.data as Record<string, unknown> | undefined) ||
      data;
    pushAlert(payload, "Alert");
    return;
  }

  if (messageType === "rewards" || messageType.startsWith("rewards.")) {
    const payload =
      (data.payload as Record<string, unknown> | undefined) ||
      (data.data as Record<string, unknown> | undefined) ||
      data;

    queryClient.invalidateQueries({ queryKey: ["rewards"] });

    const rewardKind = String(
      payload.kind ?? payload.event_type ?? payload.type ?? "",
    );
    if (rewardKind === "badge_unlock") {
      pushAlert(payload, "Badge Unlocked");
    }
    return;
  }

  if (
    messageType === "connection_established" ||
    messageType === "connection.established"
  ) {
    return;
  }

  const routedMessageType = channelType || messageType;

  // Route messages based on channel/topic/type
  switch (routedMessageType) {
    case "agents": {
      // SIG-1063: tile presence from agents.initial / agent.status / thought.
      applyOfficeAgentPresence(data);
      // Forward agent reasoning (`agent.thought`) into the Office
      // Agent-Chat feed — the frontend consumer of the /ws/agents stream.
      // Scope to the run the desk is displaying (fail-closed on missing
      // run_id) so a thought from another profile/run cannot leak in.
      const flowState = useFlowStore.getState();
      const agentMessage = agentThoughtToMessage(data, {
        allowedRunIds: [
          flowState.liveRunId,
          flowState.selectedRunId,
          flowState.currentRunInfo?.id,
        ],
      });
      if (agentMessage) {
        flowState.addMessage(agentMessage);
      }
      // Handle AI agent status updates
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-summaries"] });
      queryClient.invalidateQueries({ queryKey: ["system-health"] });
      if (
        messageType === "pnl.snapshot" ||
        messageType === "copy_trade.completed"
      ) {
        // S1 narrowed: pnl/copy events refresh position+account truth, not
        // orders/fills/risk/reconciliation.
        invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.pnl_snapshot);
      }
      break;
    }

    case "performance":
    case "analytics":
    case "strategy_optimization":
    case "strategy_monitoring":
      // Handle strategy performance and analytics updates
      queryClient.invalidateQueries({
        queryKey: ["strategies-with-instances"],
      });
      queryClient.invalidateQueries({ queryKey: ["portfolio-metrics"] });
      queryClient.invalidateQueries({ queryKey: ["portfolio-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["strategy-comparison"] });
      break;

    case "execution":
    case "processes":
    case "automation":
      // Handle execution updates using existing handler
      handleExecutionUpdates({
        data: { ...data, source: "unified" },
        queryClient,
        profileId,
        websocketData: ctx.websocketData ?? NOOP_WEBSOCKET_DATA,
      });
      // S1 narrowed: execution events move order/position/fill truth — they
      // do not touch accounts balances, risk, or reconciliation state.
      invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.execution);
      break;

    case "emergency":
    case "emergency.started":
    case "emergency.progress":
    case "emergency.position_liquidated":
    case "emergency.position_liquidation_failed":
    case "emergency.completed":
    case "emergency.error":
      applyEmergencyChannelMessage(data, queryClient, ctx.subjectScope);
      break;

    case "positions": {
      // WS-B payload-apply: position deltas write STRAIGHT into the
      // react-query cache (next-frame render) — the event already
      // carries the data; re-fetching it over REST added a full network
      // round-trip on top of the ingest cadence. Reconciliation still
      // runs as a TRAILING debounced invalidation so a burst of 14
      // follower fills costs ONE refetch, not fourteen.
      const applied = applyPositionsChannelMessage(
        data,
        queryClient as unknown as QueryClient,
      );
      if (applied) {
        const action = String(data.action ?? data.type ?? "")
          .split(".")
          .pop();
        // A validated position.opened payload is already inserted into every
        // matching v2 cache. Scheduling the broad position truth scope here
        // also refetched account caches inside the same burst window, undoing
        // the accounts-channel debounce contract. Updates/closes still take
        // the trailing reconciliation path.
        if (action !== "opened") {
          schedulePositionsReconciliation(queryClient);
        }
      } else {
        const payload = (data.payload ?? data.data ?? data) as {
          source?: unknown;
        };
        // Unsigned unmatched broker_entity opens are NOT applied (no row in
        // cache) but must not per-entity REST-stampede. Trailing 1.5s heals.
        if (payload?.source === "broker_entity") {
          schedulePositionsReconciliation(queryClient);
        } else {
          // Unknown shape or a newly-opened position we can't fully
          // construct client-side — fetch the truth now (same behavior
          // as before payload-apply, never worse), scoped to the
          // position-derived keys only.
          invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.positions);
        }
      }
      break;
    }

    case "orders":
      markGlobalFlattenUnknown(queryClient);
      // SIG-416: an execution event refreshes the fills list now, and rides
      // the TRAILING positions reconciliation (positions scope carries
      // 'accounts' + 'broker-accounts') so the KPI/Office money truth
      // re-reads after every fill while a copy fan-out burst of N fills
      // still costs ONE account refetch (BUG-A4 debounce contract).
      if (ORDER_FILL_EVENT_TYPES.has(messageType)) {
        reconcilePositionsAfterFill(queryClient);
      } else {
        invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.orders);
      }
      break;

    case "fills":
      markGlobalFlattenUnknown(queryClient);
      invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.fills);
      break;

    case "accounts":
    case "account.update":
      // BUG-A4: balance/equity deltas — direct-patch the ['accounts']
      // cache + ONE trailing (~1500ms) truth refetch per burst. The
      // dotted variant covers payloads routed by type when the channel
      // field is absent (mirrors notifications.* / reconciliation.*).
      applyAccountsChannelMessage(data, queryClient);
      break;

    case "copy_status":
    case "copy_status.initial":
    case "copy_status.snapshot":
      // FE-1 item 2: the backend published the COMPLETE copy-status snapshot
      // for one group — write it straight into the ["group-copy-status", id]
      // cache and record receipt so the 2s poll can gate. Partial payloads
      // are rejected inside the apply (they must never slow the poll).
      {
        const payload =
          (data.data as AnyRecord | undefined) ??
          (data.payload as AnyRecord | undefined);
        const snapshots = Array.isArray(payload?.snapshots)
          ? payload.snapshots
          : [payload];
        for (const snapshot of snapshots) {
          applyCopyStatusSnapshot(queryClient, snapshot);
          // Coach L17: the same snapshot carries a `ledger` key for solo-rotation
          // groups — write it into ["group-ledger", id] (BAR #12 ≤5s on fills).
          applyLedgerSnapshot(queryClient, snapshot);
        }
      }
      break;

    case "chart_features":
    case "chart_features.snapshot":
    case "chart_features.initial":
      // CV-005: a new chart snapshot revision exists. Frames carry metadata
      // only (snapshot_id/bars_sha256/fence) — invalidating the query key
      // makes consumers (useChartFeatures, CV-009) refetch the
      // REST-authoritative bytes from /market-data/snapshot/{symbol}/{tf}.
      queryClient.invalidateQueries({ queryKey: ["chart-features"] });
      break;

    case "pulse":
    case "pulse.idea":
    case "pulse.eval":
      // ONE-PULSE C8a: a new idea or per-account verdict exists. Frames are
      // wake-ups; every ["pulse", ...] query refetches the REST truth
      // (/api/v2/pulse/ideas, /api/v2/pulse/accounts/{id}/evals).
      queryClient.invalidateQueries({ queryKey: ["pulse"] });
      break;

    case "broker_health":
    case "broker.status_changed":
    case "prolonged_disconnection":
      // The channel was published from broker_health_monitor since forever but
      // was in neither VALID_CHANNELS nor the frontend list, so every broadcast
      // fanned out to zero sockets and both event types were dead letters. They
      // are the alarm that should have reported the broker token dying on
      // 2026-07-24 — the outage nobody saw for two days.
      //
      // Dotted variants are listed because these payloads are routed by `type`
      // (broadcast_to_channel sends {type, data} with no channel field), the
      // same reason notifications.* and reconciliation.* are listed here.
      invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.broker_health);
      break;

    case "notifications":
    case "notifications.initial":
    case "notifications.update":
      // SIG-221: the notifications snapshot/refresh payload
      // (notifications.initial) was silently dropped — the channel is
      // subscribed but had no case here, so it fell through to default.
      // Dotted variants cover payloads that arrive without a channel
      // field (topic/type-only routing), mirroring reconciliation.*.
      applyNotificationsChannelMessage(data, queryClient);
      break;

    case "alerts":
    case "risk_alerts":
      // Handle alerts
      pushAlert(
        (data.payload as Record<string, unknown> | undefined) ||
          (data.data as Record<string, unknown> | undefined) ||
          data,
        "System Alert",
      );
      break;

    case "market_data":
      getStoreActions().setLastMarketDataTime(Date.now());
      handleMarketDataMessage(data, queryClient);
      break;

    case "risk":
      // Handle risk metrics updates
      queryClient.invalidateQueries({ queryKey: ["risk"] });
      queryClient.invalidateQueries({ queryKey: ["risk-analytics"] });
      queryClient.invalidateQueries({ queryKey: ["correlation-matrix"] });
      break;

    case "system_broadcast":
    case "system":
    case "sandbox":
      // Handle system and sandbox updates
      queryClient.invalidateQueries({ queryKey: ["system-health"] });
      queryClient.invalidateQueries({ queryKey: ["user-sandbox"] });
      break;

    case "strategy_updates":
    case "strategy.update":
      // Handle strategy monitoring updates
      handleStrategyMonitoringUpdate(data, queryClient, profileId);
      break;

    case "signals":
    case "signals.initial":
    case "signals.update":
    case "trading_signals":
    case "signal_generated":
    case "signal_executed":
    case "signal_closed":
    case "trading_signal_update":
    case "signal_execution": // NEW: handle execution updates
      // Signals Today card (R4): the stat-card query keys on
      // ['signals-today'] — any signal event refreshes today's count.
      queryClient.invalidateQueries({ queryKey: ["signals-today"] });
      queryClient.invalidateQueries({ queryKey: ["observatory", "signals"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard", "pipeline-signals"],
      });
      // Handle trading signal updates
      handleTradingSignalUpdate(data, queryClient, profileId);
      break;

    case "reconciliation":
    case "reconciliation.mismatch_detected":
    case "reconciliation.complete":
    case "reconciliation.started":
      // PRD-82: Handle reconciliation events — S1 narrowed to the
      // reconciliation + position truth the event actually moves.
      invalidateScope(queryClient, CHANNEL_TRUTH_SCOPES.reconciliation);
      if (
        data.type === "mismatch_detected" ||
        data.type === "reconciliation.mismatch_detected"
      ) {
        const severity = data.payload?.severity || data.severity;
        if (severity === "critical" || severity === "major") {
          websocketAlertsService.addAlert(queryClient, {
            title: "Position Mismatch Detected",
            description: `${data.payload?.symbol || "Unknown"}: ${data.payload?.mismatch_type || "mismatch"} (${severity})`,
            variant: severity === "critical" ? "destructive" : "warning",
            profileId,
          });
        }
      }
      break;

    case "rewards":
      // Kept for explicit discoverability in switch-based topic routing.
      queryClient.invalidateQueries({ queryKey: ["rewards"] });
      break;

    case "ping":
      // Server liveness ping — expected traffic, nothing to route.
      // (G4 2026-07-20: this used to hit the unhandled-topic log and
      // spam the console every interval.)
      break;

    default:
      // Unknown topics stay silent in the browser console — route via debugService.
      debugService.log(
        "warning",
        `Unhandled unified WebSocket message topic: ${data.topic || data.type}`,
        data,
      );
  }

  // Update websocket message tracking
  ctx.websocketData?.updateWebsocketMessage?.(
    profileId,
    data.topic || data.type,
  );
};

/**
 * Handle strategy monitoring update messages
 */
const handleStrategyMonitoringUpdate = (
  data: any,
  queryClient: any,
  profileId: number,
): void => {
  // Handle different monitoring message structures
  const messageType = data.type || data.update_type || data.message_type;
  const messageData = data.data || data;

  switch (messageType) {
    case "strategy_update":
      // Update strategy status in real-time
      const strategyId = messageData.strategy_id || messageData.instance_id;
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "status", strategyId],
      });
      queryClient.invalidateQueries({ queryKey: ["monitoring", "summary"] });
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "active-strategies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategies-with-instances"],
      });
      break;

    case "configuration_updated":
      // Handle configuration updates (CCS-27)
      const configInstanceId = messageData.instance_id;
      // Invalidate configuration queries
      queryClient.invalidateQueries({
        queryKey: ["strategy-configuration", "config", configInstanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategy-configuration", "history", configInstanceId],
      });

      // Show notification
      websocketAlertsService.addAlert(queryClient, {
        title: "Configuration Updated",
        description: `Strategy configuration updated by ${messageData.updated_by}${messageData.update_reason ? `: ${messageData.update_reason}` : ""}`,
        variant: "info",
        profileId,
      });
      break;

    case "signal":
    case "signal_generated":
      // Handle new trading signal
      const signal = messageData.signal || messageData;

      // Show notification for high-confidence signals
      if (signal.confidence >= 0.8) {
        websocketAlertsService.addAlert(queryClient, {
          title: `${signal.signal_type || signal.type} Signal`,
          description: `${signal.symbol} at $${signal.price.toFixed(2)} (${(signal.confidence * 100).toFixed(0)}% confidence)`,
          variant: signal.signal_type === "BUY" ? "success" : "warning",
          profileId,
        });
      }

      // Invalidate relevant queries
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "signals", signal.strategy_id],
      });
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "signals-stream"],
      });
      queryClient.invalidateQueries({ queryKey: ["signal-history"] });
      queryClient.invalidateQueries({
        queryKey: ["strategies-with-instances"],
      });
      break;

    case "metrics_update":
      // Handle metrics updates
      queryClient.invalidateQueries({ queryKey: ["monitoring", "metrics"] });
      queryClient.invalidateQueries({ queryKey: ["portfolio-metrics"] });
      break;

    case "strategy_error":
      // Handle strategy errors
      const errorData = messageData;
      websocketAlertsService.addAlert(queryClient, {
        title: `Strategy Error: ${errorData.strategy_name || errorData.strategy_id}`,
        description:
          errorData.error || errorData.message || "Unknown error occurred",
        variant: "destructive",
        profileId,
      });

      // Invalidate strategy status
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "status", errorData.strategy_id],
      });
      queryClient.invalidateQueries({ queryKey: ["monitoring", "summary"] });
      queryClient.invalidateQueries({
        queryKey: ["strategies-with-instances"],
      });
      break;

    case "indicator_update":
      // Update indicator progress in real-time
      const indicatorInstanceId =
        messageData.instance_id || messageData.strategy_id;
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "status", indicatorInstanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategies-with-instances"],
      });
      break;

    case "status_change":
      // Handle strategy status change
      queryClient.invalidateQueries({ queryKey: ["monitoring", "summary"] });
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "active-strategies"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategies-with-instances"],
      });
      queryClient.invalidateQueries({
        queryKey: ["strategy-monitoring", "dashboard"],
      });

      // Notify if critical status change
      if (
        messageData.new_status === "ERROR" ||
        messageData.new_status === "error"
      ) {
        websocketAlertsService.addAlert(queryClient, {
          title: "Strategy Status Changed",
          description: `Strategy ${messageData.strategy_name || messageData.instance_id} status changed to ${messageData.new_status}: ${messageData.reason || "Unknown reason"}`,
          variant: "destructive",
          profileId,
        });
      }
      break;

    case "health_update":
      // Handle strategy health metrics update
      queryClient.invalidateQueries({
        queryKey: [
          "monitoring",
          "status",
          messageData.instance_id || messageData.strategy_id,
        ],
      });

      // Check for health alerts
      const alerts = messageData.alerts || [];
      alerts.forEach((alert: any) => {
        if (alert.level === "error" || alert.level === "warning") {
          websocketAlertsService.addAlert(queryClient, {
            title: "Strategy Health Alert",
            description: alert.message,
            variant: alert.level === "error" ? "destructive" : "warning",
            profileId,
          });
        }
      });
      break;

    default:
      debugService.log(
        "warning",
        `Unhandled strategy monitoring message type: ${messageType}`,
        data,
      );
  }
};

/**
 * Handle trading signal update messages
 */
const handleTradingSignalUpdate = (
  data: any,
  queryClient: any,
  profileId: number,
): void => {
  // Handle different signal message structures
  const messageType = data.type || data.update_type || data.message_type;
  const messageData = data.data || data;

  switch (messageType) {
    case "signals.initial":
    case "signals.update":
    case "initial":
    case "update":
      queryClient.invalidateQueries({ queryKey: ["trading-signals", "list"] });
      queryClient.invalidateQueries({ queryKey: ["signals-today"] });
      break;

    case "signal_generated":
      // Handle new trading signal generation
      const signal = messageData;

      // Show high-priority notification for high-confidence signals
      if (signal.confidence >= 0.8) {
        websocketAlertsService.addAlert(queryClient, {
          title: `🎯 ${signal.signal_type} Signal Generated`,
          description: `${signal.symbol} at $${signal.price?.toFixed(2)} (${(signal.confidence * 100).toFixed(0)}% confidence)`,
          variant:
            signal.signal_type === "BUY"
              ? "success"
              : signal.signal_type === "SELL"
                ? "warning"
                : "info",
          profileId,
        });
      }

      // Invalidate signal-related queries
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "stream"],
      });
      queryClient.invalidateQueries({ queryKey: ["trading-signals", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "analytics"],
      });
      queryClient.invalidateQueries({
        queryKey: ["monitoring", "signals-stream"],
      });

      // Update strategy-specific queries if instance_id is available
      if (signal.instance_id) {
        queryClient.invalidateQueries({
          queryKey: ["trading-signals", "strategy", signal.instance_id],
        });
        queryClient.invalidateQueries({
          queryKey: ["monitoring", "signals", signal.instance_id],
        });
      }
      break;

    case "signal_executed":
      // Handle signal execution
      const executedSignal = messageData;

      // Show execution notification
      websocketAlertsService.addAlert(queryClient, {
        title: "✅ Signal Executed",
        description: `${executedSignal.symbol} executed at $${executedSignal.execution_price?.toFixed(2)}${executedSignal.slippage ? ` (${executedSignal.slippage.toFixed(2)} slippage)` : ""}`,
        variant: "success",
        profileId,
      });

      // Update signal details and lists
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "detail", executedSignal.signal_id],
      });
      queryClient.invalidateQueries({ queryKey: ["trading-signals", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "performance"],
      });
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "analytics"],
      });
      break;

    case "signal_closed":
      // Handle signal position closure
      const closedSignal = messageData;
      const isProfitable = closedSignal.profit_loss >= 0;

      // Show closure notification with P&L
      websocketAlertsService.addAlert(queryClient, {
        title: `📊 Position Closed`,
        description: `${closedSignal.symbol}: ${isProfitable ? "+" : ""}$${closedSignal.profit_loss?.toFixed(2)} (${closedSignal.profit_loss_percentage?.toFixed(1)}%)`,
        variant: isProfitable ? "success" : "destructive",
        profileId,
      });

      // Update all signal-related queries
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "detail", closedSignal.signal_id],
      });
      queryClient.invalidateQueries({ queryKey: ["trading-signals", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "performance"],
      });
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "analytics"],
      });
      break;

    case "trading_signal_update":
      // Handle general signal status updates
      const updatedSignal = messageData;

      // Update specific signal cache
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "detail", updatedSignal.signal_id],
      });
      queryClient.invalidateQueries({ queryKey: ["trading-signals", "list"] });

      // Show notification for important status changes
      if (
        updatedSignal.status === "cancelled" ||
        updatedSignal.status === "expired"
      ) {
        websocketAlertsService.addAlert(queryClient, {
          title: "Signal Status Changed",
          description: `${updatedSignal.symbol} signal ${updatedSignal.status}`,
          variant: "info",
          profileId,
        });
      }
      break;

    case "signal_error":
      // Handle signal processing errors
      const errorData = messageData;
      websocketAlertsService.addAlert(queryClient, {
        title: "Signal Processing Error",
        description:
          errorData.error ||
          errorData.message ||
          "Unknown signal error occurred",
        variant: "destructive",
        profileId,
      });

      // Refresh signal data to reflect error state
      queryClient.invalidateQueries({ queryKey: ["trading-signals"] });
      break;

    case "signal_analytics_update":
      // Handle analytics data updates
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "analytics"],
      });
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "performance"],
      });
      break;

    case "signal_subscription_triggered":
      // Handle subscription notifications
      const subscriptionData = messageData;
      websocketAlertsService.addAlert(queryClient, {
        title: "🔔 Signal Alert",
        description: `Subscription triggered: ${subscriptionData.description || "New signal matches your criteria"}`,
        variant: "info",
        profileId,
      });

      // Refresh subscription data
      queryClient.invalidateQueries({
        queryKey: ["trading-signals", "subscriptions"],
      });
      break;

    default:
      debugService.log(
        "warning",
        `Unhandled trading signal message type: ${messageType}`,
        data,
      );
  }
};
